import React, { useState, useMemo } from 'react';
import { Check, Star, X } from 'lucide-react';
import { contrastRatio, wcagVerdict, WCAG_THRESHOLDS, toRgbString, toHex } from './colorMath';

/* ─────────────────────────────────────────────────────────────
   Every pairing in a set, shown rather than judged.

   Rows are the surface, columns are what is written on it, and the
   axis is the same on both sides: white, black, then the set. White
   and black appear as rows as well as columns because "this colour
   as text on the page" is a different question from "text on this
   colour", and a theme needs both answered.

   EVERY cell renders. Hatching the failures out hid exactly the
   information the grid exists to give — a ratio of 3.2:1 means
   nothing, while pale grey on pale blue is obvious instantly.

   The category selector changes the SAMPLE, not just the threshold.
   WCAG allows large text 3:1 where body copy needs 4.5:1, and that
   is only self-evident if the sample actually grows.

   The verdict is a tick or a cross in a dark chip in the corner.
   The chip carries its own background on purpose: a mark drawn in
   the pair's own colours is invisible precisely when it fails,
   which is the one moment it has to be readable. Analysis can be
   switched off entirely to leave nothing but the colours.
─────────────────────────────────────────────────────────────── */

const CATEGORIES = Object.entries(WCAG_THRESHOLDS).map(([id, bar]) => ({ id, ...bar }));

const WHITE = { r: 255, g: 255, b: 255, a: 1 };
const BLACK = { r: 0, g: 0, b: 0, a: 1 };

/* Stroked, not filled — the non-text rule is about making out an
   edge, and a solid shape would flatter the result. */
const Sample = ({ category }) => (category === 'nontext'
  ? <Star size={20} strokeWidth={2} />
  : 'Aa');

const ContrastMatrix = ({ colors }) => {
  const [category, setCategory] = useState('small');
  const [showAnalysis, setShowAnalysis] = useState(true);

  /* White and black lead the axis — they are the defaults everything
     else is judged against, so they belong first, not appended. */
  const axis = useMemo(() => ([
    { key: 'white', label: 'White', rgba: WHITE },
    { key: 'black', label: 'Black', rgba: BLACK },
    ...colors.map((c, i) => ({ key: `c${i}`, label: `Color ${i + 1} — ${toHex(c)}`, rgba: c })),
  ]), [colors]);

  const grid = useMemo(() => axis.map(bg => axis.map(fg => {
    const ratio = contrastRatio(fg.rgba, bg.rgba);
    return { ratio, verdict: wcagVerdict(ratio, category) };
  })), [axis, category]);

  const bar = WCAG_THRESHOLDS[category];
  const pairs = axis.length * (axis.length - 1);
  const passing = grid.reduce(
    (acc, row, r) => acc + row.filter((cell, c) => r !== c && cell.verdict !== 'Fail').length,
    0,
  );

  if (!colors.length) return <p className="stui-hint">Add colors to compare them.</p>;

  return (
    <div className="stui-ch-matrixwrap">
      <div className="stui-ch-matrixhead">
        <div className="stui-pill-group">
          {CATEGORIES.map(cat => (
            <button
              key={cat.id}
              type="button"
              className={`stui-pill${category === cat.id ? ' selected' : ''}`}
              onClick={() => setCategory(cat.id)}
              title={cat.aaa ? `AA needs ${cat.aa}:1, AAA needs ${cat.aaa}:1` : `AA needs ${cat.aa}:1, no AAA level`}
            >
              {cat.label}
            </button>
          ))}
        </div>
        <label className="stui-checkbox">
          <input
            type="checkbox"
            className="stui-checkbox-input"
            checked={showAnalysis}
            onChange={e => setShowAnalysis(e.target.checked)}
          />
          Analysis
        </label>
      </div>

      <span className="stui-hint">
        {showAnalysis
          ? `${passing} of ${pairs} pass at ${bar.aa}:1${bar.aaa ? '' : ' — this rule has no AAA level'} · hover a cell for its ratio`
          : 'Analysis hidden — just the colors · hover a cell for its ratio'}
      </span>

      <table className="stui-ch-matrix">
        <thead>
          <tr>
            <th className="stui-ch-axis" title="Rows are the background, columns are the text">
              <span className="stui-ch-axislabel">bg ↓<br />text →</span>
            </th>
            {axis.map(fg => (
              <th key={fg.key}>
                <span className="stui-ch-chip" style={{ background: toRgbString(fg.rgba) }} title={fg.label} />
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {grid.map((row, r) => (
            <tr key={axis[r].key}>
              <th scope="row">
                <span className="stui-ch-chip" style={{ background: toRgbString(axis[r].rgba) }} title={axis[r].label} />
              </th>
              {row.map((cell, c) => {
                if (r === c) {
                  return (
                    <td key={axis[c].key}>
                      <span className="stui-ch-sample na" title={`${axis[r].label} on itself — not a pairing`}>n/a</span>
                    </td>
                  );
                }
                const passes = cell.verdict !== 'Fail';
                return (
                  <td key={axis[c].key}>
                    <span
                      className={`stui-ch-sample ${category}`}
                      style={{ background: toRgbString(axis[r].rgba), color: toRgbString(axis[c].rgba) }}
                      title={`${axis[c].label} on ${axis[r].label} — ${cell.ratio.toFixed(2)}:1, ${cell.verdict} for ${bar.label.toLowerCase()}`}
                    >
                      <Sample category={category} />
                      {showAnalysis && (
                        <span className={`stui-ch-verdict ${passes ? 'pass' : 'fail'}`}>
                          {passes ? <Check size={9} strokeWidth={3} className="stui-icon-fixed" /> : <X size={9} strokeWidth={3} className="stui-icon-fixed" />}
                        </span>
                      )}
                    </span>
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default ContrastMatrix;
